import { create } from 'zustand';
import { ceAuthService } from '../api/authService';
import type { CEAuthState, CELoginRequest } from './types';

const TOKEN_KEY = 'ce_auth_token';
const TENANT_KEY = 'ce_active_tenant_id';

export const useCEAuthStore = create<CEAuthState>((set) => ({
  token: localStorage.getItem(TOKEN_KEY),
  user: null,
  tenants: [],
  activeTenantId: localStorage.getItem(TENANT_KEY),
  isAuthenticated: !!localStorage.getItem(TOKEN_KEY),
  isLoading: false,
  error: null,

  login: async (credentials: CELoginRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await ceAuthService.login(credentials);
      const { token, user } = response.data;
      localStorage.setItem(TOKEN_KEY, token);
      set({
        token,
        user,
        isAuthenticated: true,
        isLoading: false,
      });
      // Fetch tenants from /auth/me (login response doesn't include them)
      await useCEAuthStore.getState().fetchUser();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Login failed';
      const apiMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? message;
      set({ error: apiMessage, isLoading: false });
      throw error;
    }
  },

  logout: async () => {
    try {
      await ceAuthService.logout();
    } catch {
      // Ignore logout API errors - clear local state regardless
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TENANT_KEY);
      set({
        token: null,
        user: null,
        tenants: [],
        activeTenantId: null,
        isAuthenticated: false,
      });
    }
  },

  checkAuth: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    set({ isAuthenticated: !!token, token });
  },

  clearError: () => {
    set({ error: null });
  },

  setActiveTenant: (tenantId: string) => {
    localStorage.setItem(TENANT_KEY, tenantId);
    set({ activeTenantId: tenantId });
  },

  fetchUser: async () => {
    try {
      const response = await ceAuthService.me();
      const user = response.data;
      const tenants = user.tenants ?? [];
      const persisted = localStorage.getItem(TENANT_KEY);
      const firstTenant = tenants[0];
      const activeTenantId =
        persisted ?? (firstTenant ? firstTenant.slug : null);
      if (activeTenantId) {
        localStorage.setItem(TENANT_KEY, activeTenantId);
      }
      set({ user, tenants, activeTenantId });
    } catch {
      // Ignore fetch errors - user will remain null
    }
  },
}));
